<template>
  <view class="c-preview" v-if="appStore.resourcePreviewerOptions.data.length > 0">
    <view
      class="mask"
      :class="{ 'mask--fixed': appStore.resourcePreviewerOptions.display }"
      @click="handleMaskClick"
    >
      <view class="nav-wrap">
        <view class="nav-wrap__fill" :style="navFillStyle" />
        <view class="nav-wrap__content" :style="navContentStyle">
          <view class="mask-close">
            <c-img :size="48" remote-name="close_1" />
          </view>

          <view class="mask-dots">
            {{ swiperIndexRef + 1 }} / {{ appStore.resourcePreviewerOptions.data.length }}
          </view>
        </view>
      </view>
    </view>

    <view class="swiper__outer">
      <swiper
        class="swiper__comp"
        :autoplay="false"
        :current="swiperIndexRef"
        @change="handleSwiperChange"
      >
        <template v-for="(item, index) in appStore.resourcePreviewerOptions.data" :key="item.url">
          <c-img
            v-if="item.type === 'video' && !isPlayRef"
            class="source-button"
            remote-name="resource_play"
          />

          <swiper-item class="swiper__item">
            <view
              class="progress-outer"
              :class="{ 'progress-outer--fixed': appStore.resourcePreviewerOptions.display }"
              v-if="item.type === 'video'"
            >
              <progress
                class="progress"
                :percent="playTimeRef"
                :stroke-width="3"
                border-radius="12"
                activeColor="#ef6348"
                backgroundColor="#e0dede"
              />
            </view>

            <video
              v-if="item.type === 'video'"
              id="myVideo"
              class="swiper__item"
              :src="item.url"
              :controls="false"
              :show-center-play-btn="false"
              :show-progress="false"
              :enable-progress-gesture="false"
              @error="handleSourceError"
              @ended="handleSourceEnded"
              @pause="handleSourcePause"
              @play="handleSourcePlay"
              @timeupdate="handleSourceTimeUpdate"
            />
            
            <template v-else>
              <movable-area scale-area style="width: 100%; height: 100%;" :catchtouchmove="stopEventPropagation" @click="doubleClick">
                <movable-view
                  direction="all"
                  scale
                  inertia
                  :out-of-bounds="isScale == 1 ? false : true"
                  :scale-min="1"
                  :scale-max="4"
                  :scale-value="scaleRefs[index]"
                  style="width: 100%; height: 100%;"
                  @scale="handleScale"
                  @change="handleMovableChange"
                  @touchmove="handletouchmove"
                  @touchstart="handletouchstart"
                  @touchend="handletouchend"
                >
                  <c-img
                    class="swiper__item"
                    :img-mode="'aspectFit'"
                    :src="item.url || item.imgUrl"
                  />
                </movable-view>
              </movable-area>
            </template>
          </swiper-item>
        </template>
      </swiper>
    </view>
  </view>
</template>

<script setup lang="ts">
import { CLOSE_EVENT } from '@/constants';
import { useResourcePreview } from '@/hooks';
import { useAppStore } from '@/store';
import type { AnyObj } from '@/types';
import { commonUtils } from '@/utils';
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch, reactive } from 'vue';

const emits = defineEmits([CLOSE_EVENT]);

const appStore = useAppStore();

const instance = getCurrentInstance();

const swiperIndexRef = ref(0);

const systemInfo = commonUtils.navbar.get();

const rpHook = useResourcePreview();

const navFillStyle = computed(() => ({ height: `${systemInfo.menuButtonInfo.top}px` }));
const navContentStyle = computed(() => ({ height: `${systemInfo.menuButtonInfo.height}px` }));

// 竖屏
const vertical = ref(false);

// 播放进度
const playTimeRef = ref(0);
// swiper切换时，保存已有播放进度，切换回video时继续播放
// let playTimeCache = 0;
// 资源上下文
const sourceCtx = ref<UniApp.VideoContext>();
// 是否播放状态
const isPlayRef = ref(false);

let time: any; //定时器
const startTime = ref(0);
const isScale = ref(1);
// 为每个图片单独管理缩放状态
const scaleRefs = ref<number[]>([]);
const old = reactive({
  scale: 1
})
const isZoom = ref(false);
const lock = ref(false);
const elePosition = ref('暂无位置');
const isTouchOutOfBounds = ref('');
const isOutOfBounds = ref('');
let lastTapTimeoutFunc: any;
const lastTapDiffTime = ref(0);
const startData = reactive({ clientX: 0, clientY: 0 });

const handleMaskClick = () => {
  rpHook.resourcePreview({
    data: [],
    defaultIndex: 0,
    display: false,
  });
  emits(CLOSE_EVENT);
};

/** 视频加载错误 */
const handleSourceError = () => {
  console.log('<资源加载错误>');
};

/** 视频播放结束 */
const handleSourceEnded = () => {
  playTimeRef.value < 100 && (playTimeRef.value = 100);
  setTimeout(() => {
    sourceCtx.value?.play();
  }, 300);
};

/** 视频播放中断、停止 */
const handleSourcePause = () => {
  isPlayRef.value = false;
};

/** 视频播放开始 */
const handleSourcePlay = () => {
  isPlayRef.value = true;
};

/** 播放时间更新 */
const handleSourceTimeUpdate = (e: AnyObj) => {
  const { duration, currentTime } = e.detail;
  const percent = Math.ceil((currentTime / duration) * 100);
  playTimeRef.value = percent > 100 ? 100 : percent;
};

const stopEventPropagation = () => {

}

/** 监听轮播滚动变化 */
const handleSwiperChange = async (e: { detail: { current: number } }) => {
  const current = e.detail.current;
  swiperIndexRef.value = current;

  resetZoomAndPos();

  if (current === 0) {
    sourceCtx.value?.play();
  } else {
    sourceCtx.value?.pause();
  }
};

const handletouchstart = (e: any) => {
  if (isZoom.value) {
    isZoom.value = false;
  }
  // 获取初始时间
  startTime.value = Date.now();
  // 获取初始的位置
  startData.clientX = e.changedTouches[0].clientX;
  startData.clientY = e.changedTouches[0].clientY;

  var tchs = e.touches.length;
  if (tchs != 1) {
    return false;
  }
  time = setTimeout(() => {
    onLongPress(e);
  }, 1000);
  return false;
}

const handletouchend = (event: any) => {
  // 如果图片缩放过，直接跳出（图片缩放后轮播图片会导致此方法执行两次，具体原因不明[可能我笨没发现😂]）
  // 这边用isZoom拦截一下
  if (isZoom.value) {
    return;
  }

  const endTime = Date.now()
  if (endTime - startTime.value > 2000) {
    // 如果手指滑动的距离超过2s 就默认不合法
    return;
  }
  elePosition.value = '暂无位置'
  // 记录手指滑动的方向
  const subX = event.changedTouches[0].clientX - startData.clientX;
  const subY = event.changedTouches[0].clientY - startData.clientY;
  if (subY > 50 || subY < -50) {
    if (subY > 50) {
      console.log('下滑');
      elePosition.value = '下滑';
    } else if (subY < -50) {
      console.log('上滑');
      elePosition.value = '上滑';
    }
  } else {
    if (subX > 100 / isScale.value) {
      console.log('右滑');
      elePosition.value = '右滑';
    } else if (subX < -100 / isScale.value) {
      console.log('左滑');
      elePosition.value = '左滑';
    }
  }
  
  if (isTouchOutOfBounds.value && isOutOfBounds.value) {
    lock.value = false;
  }
  
  if (vertical.value) { // 竖屏
    if (elePosition.value === '下滑' && !lock.value) {
      if (swiperIndexRef.value === 0) {
        swiperIndexRef.value = appStore.resourcePreviewerOptions.data.length - 1;
      } else {
        swiperIndexRef.value = swiperIndexRef.value - 1;
      }
    } else if (elePosition.value === '上滑' && !lock.value) {
      if (swiperIndexRef.value === appStore.resourcePreviewerOptions.data.length - 1) {
        swiperIndexRef.value = 0;
      } else {
        swiperIndexRef.value = swiperIndexRef.value + 1;
      }
    }
  } else { // 横屏
    if (elePosition.value === '右滑' && !lock.value) {
      if (swiperIndexRef.value === 0) {
        swiperIndexRef.value = appStore.resourcePreviewerOptions.data.length - 1;
      } else {
        swiperIndexRef.value = swiperIndexRef.value - 1;
      }
    } else if (elePosition.value === '左滑' && !lock.value) {
      if (swiperIndexRef.value === appStore.resourcePreviewerOptions.data.length - 1) {
        swiperIndexRef.value = 0;
      } else {
        swiperIndexRef.value = swiperIndexRef.value + 1;
      }
    }
  }

  clearTimeout(time);
  if (time !== 0) {
    //处理点击时间
  }
  return false;
}

//清除定时器
const handletouchmove = () => {
  clearTimeout(time);
  time = 0;
}

// 处理长按事件
const onLongPress = (e: any) => {
  var src = e.currentTarget.dataset.src;
  var index = e.currentTarget.dataset.index;
  var data = {
    src: src,
    index: index
  };
  // $emit('longPress', data);
}

const handleScale = (e: { detail: { scale: number } }) => {
  isScale.value = e.detail.scale;
  old.scale = e.detail.scale;
}

const handleMovableChange = (e: { detail: { x: number; y: number; source: string } }) => {
  if (isScale.value === 1) {
    lock.value = false; // 允许滑动
  } else {
    lock.value = true; // 不允许滑动
  }
  
  if (isScale.value !== 1) {
    if (!isTouchOutOfBounds.value && e.detail.source === 'touch-out-of-bounds') {
      isTouchOutOfBounds.value = 'touch-out-of-bounds';
    } else if (!isOutOfBounds.value && e.detail.source === 'out-of-bounds') {
      isOutOfBounds.value = 'out-of-bounds';
    } else if (e.detail.source === 'touch') {
      // 正常拖动时重置边界状态
      isTouchOutOfBounds.value = '';
      isOutOfBounds.value = '';
    }
  }
}

const resetZoomAndPos = async () => {
  isZoom.value = false;
	lock.value = false;
  isScale.value = 1;
  if (["左滑", "上滑"].includes(elePosition.value)) {
    if (swiperIndexRef.value === 0) {
      scaleRefs.value[appStore.resourcePreviewerOptions.data.length - 1] = old.scale;
      await nextTick(() => {
        scaleRefs.value[appStore.resourcePreviewerOptions.data.length - 1] = 1;
      });
    } else {
      scaleRefs.value[swiperIndexRef.value - 1] = old.scale;
      await nextTick(() => {
        scaleRefs.value[swiperIndexRef.value - 1] = 1;
      });
    }
  } else if (["右滑", "下滑"].includes(elePosition.value)) {
    if (swiperIndexRef.value === appStore.resourcePreviewerOptions.data.length - 1) {
      scaleRefs.value[0] = old.scale;
      await nextTick(() => {
        scaleRefs.value[0] = 1;
      });
    } else {
      scaleRefs.value[swiperIndexRef.value + 1] = old.scale;
      await nextTick(() => {
        scaleRefs.value[swiperIndexRef.value + 1] = 1;
      });
    }
  }
  isTouchOutOfBounds.value = '';
  isOutOfBounds.value = '';
  old.scale = 1;
}

// 自定义双击事件
const doubleClick = (e: any) => {
  lock.value = true;
  
  let curTime = new Date().getTime();
  let lastTime = lastTapDiffTime.value;
  lastTapDiffTime.value = curTime;
  //两次点击间隔小于300ms, 认为是双击
  let diff = curTime - lastTime;
  if (diff < 300) {
    console.log("双击")
    // 如果图片当前没有放大则双击放大两倍
    if (isScale.value == 1) {
      isScale.value = 2;
      scaleRefs.value[swiperIndexRef.value] = 2;
      old.scale = 2;
    } else {
      isScale.value = 1;
      scaleRefs.value[swiperIndexRef.value] = 1;
      old.scale = 1;
    }
    
    clearTimeout(lastTapTimeoutFunc); // 成功触发双击事件时，取消单击事件的执行
  } else {
    // 单击事件延时300毫秒执行
    lastTapTimeoutFunc = setTimeout(() => {
      console.log("单击");
    }, 300);
  }
}

watch(
  () => appStore.resourcePreviewerOptions.defaultIndex,
  (index) => {
    if (index !== undefined && index >= 0) {
      swiperIndexRef.value = index;
    }
  }
);

watch(
  () => isScale.value,
  (newVal, objVal) => {
    if (newVal > 1.000001) {
      lock.value = true // 当图片缩放值大于1时，不允许滑动
    } else {
      lock.value = false
      if (objVal > 1) {
        isZoom.value = true;
      }
    }
  }, {
    deep: true
  }
);

// 初始化缩放状态数组
watch(
  () => appStore.resourcePreviewerOptions.data,
  (newData) => {
    if (newData && newData.length > 0) {
      scaleRefs.value = new Array(newData.length).fill(1);
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await nextTick();

  const ctx = uni.createVideoContext('myVideo', instance);
  if (ctx) {
    sourceCtx.value = ctx;
    appStore.resourcePreviewerOptions.defaultIndex === 0 && ctx.play();
  }
});
</script>

<style scoped lang="scss">
.c-preview {
  position: relative;
  z-index: 9999;

  .mask {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 1);
    z-index: -1;
    opacity: 0;
    // transition: all 300ms ease;

    .nav-wrap {
      position: relative;
      width: 100%;
    }

    .nav-wrap__fill {
      width: 100%;
    }

    .nav-wrap__content {
      position: relative;
      width: 100%;
    }

    &.mask--fixed {
      z-index: 999;
      opacity: 1;
    }

    .mask-close {
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
    }

    .mask-dots {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 24rpx;
      font-weight: bold;
      color: $W;
    }
  }

  .swiper__outer {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 1002;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 75vh;

    .swiper__comp {
      width: 100%;
      height: 100%;
    }
    .swiper__item {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }
  }

  .progress-outer {
    position: absolute;
    bottom: 0rpx;
    left: 50%;
    z-index: 999;
    transform: translateX(-50%);
    width: 100%;
  }

  .source-button {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 99;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 140rpx;
    height: 140rpx;
    border-radius: 50%;
  }
}
</style>
